import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SavedWorkflow } from '../types/workflow';
import { deleteWorkflow, createWorkflow, exportWorkflowJson, executeWorkflow, getExecutionLogs, getLiveLogs, activateWebhook, deactivateWebhook } from '../services/workflowService';
import { StorageFile, listStorageFiles, uploadFiles, downloadFile, deleteFile } from '../services/storageService';

interface FlowListProps {
    workflows: SavedWorkflow[];
    onCreateNew: () => void;
    onEdit: (id: string) => void;
    onRefresh: () => void;
}

const FlowList: React.FC<FlowListProps> = ({ workflows, onCreateNew, onEdit, onRefresh }) => {
    const WORKFLOWS_PER_PAGE = 9;
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [executingIds, setExecutingIds] = useState<Set<string>>(new Set());
    const [logsModal, setLogsModal] = useState<{ workflowName: string; executions: any[] } | null>(null);
    const [showAllExecutions, setShowAllExecutions] = useState(false);
    const [loadingLogsId, setLoadingLogsId] = useState<string | null>(null);
    const [togglingWebhookId, setTogglingWebhookId] = useState<string | null>(null);
    const [storageModal, setStorageModal] = useState<{ workflowId: string; workflowName: string } | null>(null);

    const filtered = workflows.filter(w =>
        w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        w.url.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.max(1, Math.ceil(filtered.length / WORKFLOWS_PER_PAGE));
    const pageStart = (currentPage - 1) * WORKFLOWS_PER_PAGE;
    const pageEnd = pageStart + WORKFLOWS_PER_PAGE;
    const paginatedWorkflows = filtered.slice(pageStart, pageEnd);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    useEffect(() => {
        setCurrentPage(prev => Math.min(prev, totalPages));
    }, [totalPages]);

    const handleDelete = async (id: string, name: string) => {
        if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
            try {
                await deleteWorkflow(id);
                onRefresh();
            } catch (err) {
                console.error('Failed to delete workflow:', err);
                alert('Failed to delete workflow.');
            }
        }
    };

    const handleExport = (workflow: SavedWorkflow) => {
        const json = exportWorkflowJson(workflow);
        const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${workflow.name.replace(/\s+/g, '_').toLowerCase()}.json`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleDuplicate = async (workflow: SavedWorkflow) => {
        try {
            await createWorkflow({
                name: `${workflow.name} (Copy)`,
                url: workflow.url,
                actions: workflow.actions,
                nodes: workflow.nodes,
                edges: workflow.edges,
            });
            onRefresh();
        } catch (err) {
            console.error('Failed to duplicate workflow:', err);
            alert('Failed to duplicate workflow.');
        }
    };

    const handleExecute = async (workflow: SavedWorkflow) => {
        if (executingIds.has(workflow.id)) return;

        try {
            setExecutingIds(prev => new Set(prev).add(workflow.id));
            const { executionId } = await executeWorkflow(workflow.id);

            // Poll execution status until done
            const poll = async () => {
                try {
                    const executions = await getExecutionLogs(workflow.id);
                    const exec = executions.find((e: any) => e.id === executionId);
                    if (exec && exec.status !== 'running') {
                        setExecutingIds(prev => {
                            const next = new Set(prev);
                            next.delete(workflow.id);
                            return next;
                        });
                        // Update modal if it's open for this workflow
                        setLogsModal(prev => prev && prev.workflowName === workflow.name ? { ...prev, executions } : prev);
                        onRefresh();
                        return;
                    }
                    setTimeout(poll, 2000);
                } catch {
                    setTimeout(poll, 3000);
                }
            };
            setTimeout(poll, 2000);
        } catch (err: any) {
            console.error('Failed to execute workflow:', err);
            alert(`Failed to execute: ${err.message}`);
            setExecutingIds(prev => {
                const next = new Set(prev);
                next.delete(workflow.id);
                return next;
            });
        }
    };

    const handleViewLogs = async (workflow: SavedWorkflow) => {
        setLoadingLogsId(workflow.id);
        try {
            const executions = await getExecutionLogs(workflow.id);
            // Ensure logs field is always an array (pg may return string for jsonb)
            for (const exec of executions) {
                if (typeof exec.logs === 'string') {
                    try { exec.logs = JSON.parse(exec.logs); } catch { exec.logs = []; }
                }
                if (!exec.logs) exec.logs = [];
            }
            setLogsModal({ workflowName: workflow.name, executions });
        } catch (err) {
            alert('Failed to load execution logs.');
        } finally {
            setLoadingLogsId(null);
        }
    };

    const hasWebhookAsFirstNode = (workflow: SavedWorkflow): boolean =>
        workflow.actions?.[0]?.type === 'webhook';

    const handleToggleWebhook = async (workflow: SavedWorkflow) => {
        setTogglingWebhookId(workflow.id);
        try {
            if (workflow.webhookActive) {
                await deactivateWebhook(workflow.id);
            } else {
                await activateWebhook(workflow.id);
            }
            onRefresh();
        } catch (err: any) {
            alert(`Failed to toggle webhook: ${err.message}`);
        } finally {
            setTogglingWebhookId(null);
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getWorkflowUrl = (workflow: SavedWorkflow): string => {
        if (workflow.url && workflow.url.trim() !== '') return workflow.url;
        const openBrowserAction = workflow.actions.find(a => a.type === 'open_browser');
        if (openBrowserAction?.url) return openBrowserAction.url;
        return '';
    };

    const ACTION_ICONS: Record<string, string> = {
        open_browser: '🌍',
        close_browser: '❌',
        click: '👆',
        hover: '🖱️',
        write: '✍️',
        sleep: '⏱️',
        get_element: '🔍',
        get_elements: '📋',
        while: '🔄',
        loop: '➰',
        if: '❓',
        api_call: '🌐',
        switch_to_new_window: '🪟',
        switch_to_frame: '🖼️',
        code: '💻',
        webhook: '🪝',
        last_file: '📄',
        wait_for_url_change: '🔗',
        wait_for_element: '⏳',
        create_file: '📝',
        read_file: '📖',
        rename_file: '✏️',
        ai_agent: '🤖',
    };

    const countAllActions = (actions: any[]): number => {
        let count = 0;
        for (const a of actions) {
            count++;
            if (a.actions && a.actions.length > 0) count += countAllActions(a.actions);
        }
        return count;
    };

    const getActionIcons = (workflow: SavedWorkflow): string => {
        if (!workflow.actions || workflow.actions.length === 0) return '(no actions)';
        let remaining = 12;

        const iconFor = (a: any): string | null => {
            if (remaining <= 0) return null;
            remaining--;
            const icon = ACTION_ICONS[a.type] || '⚙️';
            if ((a.type === 'loop' || a.type === 'if' || a.type === 'while') && a.actions && a.actions.length > 0) {
                const subIcons: string[] = [];
                for (const s of a.actions) {
                    const si = iconFor(s);
                    if (si === null) break;
                    subIcons.push(si);
                }
                return `${icon}(${subIcons.join('')})`;
            }
            return icon;
        };

        const icons: string[] = [];
        for (const a of workflow.actions) {
            const ic = iconFor(a);
            if (ic === null) break;
            icons.push(ic);
        }
        return icons.join(' ');
    };

    return (
        <div className="flow-list-page">
            <div className="flow-list-header">
                <div>
                    <h1>🤖 RPA Workflow Designer</h1>
                    <p className="flow-list-subtitle">Create and manage your automation workflows</p>
                </div>
                <button className="btn-create" onClick={onCreateNew}>+ Create New Workflow</button>
            </div>

            <div className="flow-list-toolbar">
                <input
                    type="text"
                    className="flow-search"
                    placeholder="Search workflows..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="flow-count">
                    {filtered.length} workflow(s)
                    {filtered.length > 0 && ` • Showing ${pageStart + 1}-${Math.min(pageEnd, filtered.length)}`}
                </span>
            </div>

            {logsModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }} onClick={() => { setLogsModal(null); setShowAllExecutions(false); }} />
                    <div style={{ position: 'relative', background: 'white', borderRadius: 8, width: '85%', maxWidth: 900, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                        <div style={{ padding: '16px 20px', background: '#37474F', color: 'white', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <h3 style={{ margin: 0, fontSize: 16 }}>💻 Execution Logs — {logsModal.workflowName}</h3>
                            <button onClick={() => { setLogsModal(null); setShowAllExecutions(false); }} style={{ background: 'none', border: 'none', color: 'white', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                        </div>
                        <div style={{ overflowY: 'auto', flex: 1, padding: 20 }}>
                            {logsModal.executions.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>
                                    <div style={{ fontSize: 48 }}>📋</div>
                                    <p>No executions yet for this workflow.</p>
                                </div>
                            ) : (
                                <>
                                    {(() => {
                                        const running = logsModal.executions.filter(e => e.status === 'running');
                                        const finished = logsModal.executions.filter(e => e.status !== 'running');
                                        const visibleFinished = showAllExecutions ? finished : finished.slice(-3);
                                        const hidden = finished.length - visibleFinished.length;
                                        return (
                                            <>
                                                {hidden > 0 && (
                                                    <div style={{ textAlign: 'center', marginBottom: 12 }}>
                                                        <button
                                                            onClick={() => setShowAllExecutions(true)}
                                                            style={{ background: 'none', border: '1px solid #ccc', borderRadius: 4, color: '#607D8B', cursor: 'pointer', fontSize: 12, padding: '4px 14px' }}
                                                        >
                                                            ▲ {hidden} more executions
                                                        </button>
                                                    </div>
                                                )}
                                                {[...visibleFinished, ...running].map((exec, i) => (
                                                    <ExecutionEntry key={exec.id || i} exec={exec} />
                                                ))}
                                            </>
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {storageModal && (
                <StorageModal
                    workflowId={storageModal.workflowId}
                    workflowName={storageModal.workflowName}
                    onClose={() => setStorageModal(null)}
                />
            )}

            {filtered.length === 0 ? (
                <div className="flow-list-empty">
                    {workflows.length === 0 ? (
                        <>
                            <div className="empty-icon">📋</div>
                            <h2>No workflows yet</h2>
                            <p>Get started by creating your first automation workflow.</p>
                            <button className="btn-create" onClick={onCreateNew}>+ Create New Workflow</button>
                        </>
                    ) : (
                        <>
                            <div className="empty-icon">🔍</div>
                            <h2>No results found</h2>
                            <p>No workflows match your search term.</p>
                        </>
                    )}
                </div>
            ) : (
                <>
                <div className="flow-list-grid">
                    {paginatedWorkflows.map(workflow => (
                        <div key={workflow.id} className="flow-card">
                            <div className="flow-card-header">
                                <h3 className="flow-card-title">{workflow.name}</h3>
                                <span className="flow-card-actions-count">
                                    {countAllActions(workflow.actions)} action(s)
                                </span>
                            </div>
                            <div className="flow-card-body">
                                <div className="flow-card-url" title={workflow.actions.map(function summarize(a: any): string {
                                    const label = a.type;
                                    if ((a.type === 'loop' || a.type === 'if' || a.type === 'while') && a.actions && a.actions.length > 0) {
                                        return `${label}(${a.actions.map((s: any) => summarize(s)).join(' → ')})`;
                                    }
                                    return label;
                                }).join(' → ')}>
                                    {getActionIcons(workflow)}
                                    {countAllActions(workflow.actions) > 12 && <span style={{ fontSize: 11, color: '#aaa', marginLeft: 4 }}>+{countAllActions(workflow.actions) - 12} more</span>}
                                </div>
                                <div className="flow-card-meta">
                                    <span>Created: {formatDate(workflow.createdAt)}</span>
                                    <span>Updated: {formatDate(workflow.updatedAt)}</span>
                                    <span>
                                        {workflow.lastExecutedAt
                                            ? `🕐 Last executed: ${formatDate(workflow.lastExecutedAt)}`
                                            : '🕐 Never executed'}
                                    </span>
                                </div>
                            </div>
                            <div className="flow-card-footer">
                                {hasWebhookAsFirstNode(workflow) && (
                                    <button
                                        onClick={() => handleToggleWebhook(workflow)}
                                        disabled={togglingWebhookId === workflow.id}
                                        title={workflow.webhookActive ? 'Webhook active — click to deactivate' : 'Click to activate webhook listener'}
                                        style={{
                                            padding: '6px 12px',
                                            border: 'none',
                                            borderRadius: 4,
                                            background: workflow.webhookActive ? '#00695C' : '#90A4AE',
                                            color: 'white',
                                            cursor: togglingWebhookId === workflow.id ? 'wait' : 'pointer',
                                            fontSize: 12,
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 4,
                                        }}
                                    >
                                        {togglingWebhookId === workflow.id ? '⏳' : workflow.webhookActive ? '🟢 Active' : '⚫ Activate'}
                                        {' '}🪝
                                    </button>
                                )}
                                <button
                                    className="btn-execute"
                                    onClick={() => handleExecute(workflow)}
                                    disabled={executingIds.has(workflow.id) || workflow.actions.length === 0 || hasWebhookAsFirstNode(workflow)}
                                    title={hasWebhookAsFirstNode(workflow) ? 'This workflow is triggered by webhook, not manually' : ''}
                                >
                                    {executingIds.has(workflow.id) ? '⏳ Running...' : '▶ Execute'}
                                </button>
                                <button className="btn-edit" onClick={() => onEdit(workflow.id)}>✏️ Edit</button>
                                <button
                                    onClick={() => handleViewLogs(workflow)}
                                    disabled={loadingLogsId === workflow.id}
                                    style={{ padding: '6px 12px', border: '1px solid #607D8B', borderRadius: 4, background: 'white', cursor: 'pointer', fontSize: 12, color: '#607D8B' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#607D8B'; (e.currentTarget as HTMLButtonElement).style.color = 'white'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'white'; (e.currentTarget as HTMLButtonElement).style.color = '#607D8B'; }}
                                >
                                    {loadingLogsId === workflow.id ? '⏳' : '📋 Logs'}
                                </button>
                                <button
                                    onClick={() => setStorageModal({ workflowId: workflow.id, workflowName: workflow.name })}
                                    style={{ padding: '6px 12px', border: '1px solid #6d4c41', borderRadius: 4, background: 'white', cursor: 'pointer', fontSize: 12, color: '#6d4c41' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#6d4c41'; (e.currentTarget as HTMLButtonElement).style.color = 'white'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'white'; (e.currentTarget as HTMLButtonElement).style.color = '#6d4c41'; }}
                                >
                                    📁 Storage
                                </button>
                                <button className="btn-export" onClick={() => handleExport(workflow)}>📥 Export</button>
                                <button className="btn-duplicate" onClick={() => handleDuplicate(workflow)}>📋 Duplicate</button>
                                <button className="btn-delete" onClick={() => handleDelete(workflow.id, workflow.name)}>🗑️ Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
                {totalPages > 1 && (
                    <div className="flow-pagination" role="navigation" aria-label="Workflow pages">
                        <button
                            className="flow-pagination-btn"
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                        >
                            First
                        </button>
                        <button
                            className="flow-pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span className="flow-pagination-status">Page {currentPage} of {totalPages}</span>
                        <button
                            className="flow-pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                        <button
                            className="flow-pagination-btn"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                        >
                            Last
                        </button>
                    </div>
                )}
                </>
            )}
        </div>
    );
};

const StorageModal: React.FC<{ workflowId: string; workflowName: string; onClose: () => void }> = ({ workflowId, workflowName, onClose }) => {
    const [files, setFiles] = useState<StorageFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            setFiles(await listStorageFiles(workflowId));
        } catch { }
        setLoading(false);
    }, [workflowId]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleUpload = async (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        try {
            await uploadFiles(workflowId, Array.from(fileList));
            await refresh();
        } catch (err) {
            console.error('Upload failed:', err);
        }
    };

    const handleDelete = async (filename: string) => {
        try {
            await deleteFile(workflowId, filename);
            setFiles(prev => prev.filter(f => f.name !== filename));
        } catch (err) {
            console.error('Delete failed:', err);
        }
    };

    const formatSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }} onClick={onClose} />
            <div style={{ position: 'relative', background: 'white', borderRadius: 8, width: '80%', maxWidth: 750, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                <div style={{ padding: '16px 20px', background: '#6d4c41', color: 'white', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <h3 style={{ margin: 0, fontSize: 16 }}>📁 Storage — {workflowName}</h3>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={() => fileInputRef.current?.click()} style={{ padding: '4px 12px', background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}>
                            📤 Upload
                        </button>
                        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => { handleUpload(e.target.files); e.target.value = ''; }} />
                        <button onClick={refresh} style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                            🔄
                        </button>
                        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                    </div>
                </div>
                <div
                    style={{ overflowY: 'auto', flex: 1, position: 'relative' }}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
                >
                    {loading ? (
                        <div style={{ color: '#aaa', padding: 40, textAlign: 'center' }}>Loading...</div>
                    ) : files.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#aaa', padding: 40, border: dragOver ? '2px dashed #6d4c41' : '2px dashed transparent', borderRadius: 8, margin: 16, transition: 'border 0.2s' }}>
                            <div style={{ fontSize: 48 }}>📁</div>
                            <p>No files yet. Drag & drop files here or click <strong>📤 Upload</strong>.</p>
                            <p style={{ fontSize: 12 }}>Browser downloads during execution will also appear here.</p>
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
                                    <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#555' }}>File</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#555', width: 90 }}>Size</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#555', width: 160 }}>Modified</th>
                                    <th style={{ width: 90 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {files.map(f => (
                                    <tr key={f.name} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '8px 16px', fontFamily: 'monospace', color: '#333' }}>📄 {f.name}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#888' }}>{formatSize(f.size)}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#888', fontSize: 12 }}>{new Date(f.modified).toLocaleString()}</td>
                                        <td style={{ padding: '8px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            <button onClick={() => downloadFile(workflowId, f.name)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '2px 6px' }} title="Download">⬇️</button>
                                            <button onClick={() => handleDelete(f.name)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '2px 6px', color: '#f44336' }} title="Delete">🗑️</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {dragOver && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(109,76,65,0.12)', border: '3px dashed #6d4c41', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#6d4c41', fontWeight: 'bold', pointerEvents: 'none' }}>
                            Drop files to upload
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ExecutionEntry: React.FC<{ exec: any }> = ({ exec }) => {
    const [open, setOpen] = useState(exec.status === 'running');
    const [liveLogs, setLiveLogs] = useState<any[]>(exec.logs || []);
    const [liveStatus, setLiveStatus] = useState(exec.status);
    const offsetRef = useRef(0);
    const logEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setLiveLogs(exec.logs || []);
        setLiveStatus(exec.status);
        offsetRef.current = 0;
    }, [exec.id, exec.status]);

    useEffect(() => {
        if (liveStatus !== 'running' || !open) return;
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            try {
                const result = await getLiveLogs(exec.id, offsetRef.current);
                if (cancelled) return;
                if (result.logs.length > 0) {
                    setLiveLogs(prev => [...prev, ...result.logs]);
                    offsetRef.current = result.offset;
                }
                if (result.finished) {
                    setLiveStatus(result.status);
                } else {
                    setTimeout(poll, 1000);
                }
            } catch {
                if (!cancelled) setTimeout(poll, 2000);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [liveStatus, open, exec.id]);

    useEffect(() => {
        if (open) logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [liveLogs, open]);

    const statusColor = liveStatus === 'success' ? '#4CAF50' : liveStatus === 'running' ? '#FF9800' : '#f44336';
    const statusIcon = liveStatus === 'success' ? '✅' : liveStatus === 'running' ? '⏳' : '❌';

    const formatDate = (d: string) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

    const displayLogs = liveStatus === 'running' ? liveLogs : (exec.logs || []);

    return (
        <div style={{ border: '1px solid #ddd', borderRadius: 6, marginBottom: 12, overflow: 'hidden' }}>
            <div
                onClick={() => setOpen(o => !o)}
                style={{ padding: '10px 16px', background: '#f5f5f5', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}
            >
                <span style={{ fontSize: 16 }}>{statusIcon}</span>
                <span style={{ fontWeight: 'bold', color: statusColor, minWidth: 70, fontSize: 13 }}>{liveStatus}</span>
                <span style={{ color: '#555', fontSize: 12 }}>{formatDate(exec.startedAt)}</span>
                {exec.durationMs != null && (
                    <span style={{ color: '#888', fontSize: 11 }}>⏱ {(exec.durationMs / 1000).toFixed(1)}s</span>
                )}
                {liveStatus === 'running' && (
                    <span style={{ color: '#FF9800', fontSize: 11, fontWeight: 'bold', animation: 'pulse 1.5s infinite' }}>● LIVE</span>
                )}
                {exec.error && (
                    <span style={{ color: '#f44336', fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {exec.error}
                    </span>
                )}
                <span style={{ marginLeft: 'auto', color: '#aaa', fontSize: 12 }}>{open ? '▲ Hide' : '▼ Show'} logs</span>
            </div>
            {open && (
                <div style={{ background: '#1e1e1e', maxHeight: 320, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                    {displayLogs.length === 0 ? (
                        <div style={{ color: '#555', padding: 16 }}>{liveStatus === 'running' ? 'Waiting for logs...' : 'No log entries.'}</div>
                    ) : (
                        displayLogs.map((entry: any, idx: number) => (
                            <div key={idx} style={{ padding: '3px 12px', borderBottom: '1px solid #2a2a2a', display: 'flex', gap: 10 }}>
                                <span style={{ color: '#555', fontSize: 10, whiteSpace: 'nowrap', paddingTop: 2 }}>
                                    {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString('en-US', { hour12: false }) : ''}
                                </span>
                                <span style={{
                                    color: (entry.message || '').startsWith('Error') ? '#ff5252' : '#69f0ae',
                                    whiteSpace: 'pre-wrap', wordBreak: 'break-all'
                                }}>
                                    {entry.message || String(entry)}
                                </span>
                            </div>
                        ))
                    )}
                    <div ref={logEndRef} />
                </div>
            )}
        </div>
    );
};

export default FlowList;
